/**
 * T-207: the "Add as a contact" email links to /portal/access-request#t=<token>. An inline script
 * in index.html moves the token out of the address bar before any app code or analytics loads
 * (window.__htPortalAccessRequestToken). This keeps it for this tab only (sessionStorage, plus
 * memory if storage is blocked) so it survives the sign-in hop, the same pattern as the alert
 * email links (alertActionFragment.ts). A fragment never reaches a server or a log.
 */
export const ACCESS_REQUEST_TOKEN_KEY = 'ht:portal-access-request';

const PAGE = /^\/portal\/access-request\/?$/;
const TOKEN = /^[A-Za-z0-9_-]{43}$/;

type StashWindow = Window & { __htPortalAccessRequestToken?: string };
type Loc = Pick<Location, 'hash' | 'pathname' | 'search'>;
type Hist = Pick<History, 'replaceState' | 'state'>;

let inMemory: string | null = null;

function keep(token: string): void {
  inMemory = token;
  try {
    sessionStorage.setItem(ACCESS_REQUEST_TOKEN_KEY, token);
  } catch {
    // storage blocked: the in-memory copy still serves this page view
  }
}

/** A link opened in this same tab (no reload, so index.html didn't run): take its token. */
export function captureAccessRequestFragment(loc: Loc = window.location, hist: Hist = window.history): void {
  if (!PAGE.test(loc.pathname) || !loc.hash) return;
  try {
    // A new link replaces any earlier one, even when the new one is malformed.
    forgetAccessRequestToken();
    const token = new URLSearchParams(loc.hash.slice(1)).get('t');
    if (token && TOKEN.test(token)) keep(token);
  } finally {
    hist.replaceState(hist.state ?? null, '', loc.pathname + loc.search);
  }
}

/** The token for this tab, if one was captured (index.html's stash is taken once). */
export function readAccessRequestToken(): string | null {
  const w = window as StashWindow;
  const early = w.__htPortalAccessRequestToken;
  if (early !== undefined) {
    delete w.__htPortalAccessRequestToken;
    if (TOKEN.test(early)) keep(early);
  }
  try {
    return sessionStorage.getItem(ACCESS_REQUEST_TOKEN_KEY) ?? inMemory;
  } catch {
    return inMemory;
  }
}

/** Done with the request: forget its token in this tab. */
export function forgetAccessRequestToken(): void {
  inMemory = null;
  delete (window as StashWindow).__htPortalAccessRequestToken;
  try {
    sessionStorage.removeItem(ACCESS_REQUEST_TOKEN_KEY);
  } catch {
    // storage blocked: nothing was kept there
  }
}
